import { FormEvent, useEffect, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useAuthStore } from '../store/auth'
import { useEvent, useEventOccurrences } from '../hooks/usePotlucks'
import { useUpdateEvent, useDeleteEvent } from '../hooks/useChurchAdmin'
import { useChurch } from '../hooks/useChurches'
import { geocodeAddress } from '../lib/geocode'
import { computeRsvpClosesAt, deriveRsvpClose, type RsvpCloseOption } from '../lib/rsvpClose'
import { ApiError } from '../lib/api'
import { PublicLayout } from '../components/layout/PublicLayout'
import { EventBadge } from '../components/ui/EventBadge'
import { Icons } from '../components/ui/Icons'
import LocationPicker from '../components/LocationPicker'
import type { EventType, EventAttributes, DishGoalCategory } from '@fellowship/shared'
import { EventTimePicker } from '../components/ui/DateTimePicker'
import { SectionCard } from '../components/event/EventFormPrimitives'
import { BasicsFields } from '../components/event/BasicsFields'
import { VisibilityPicker } from '../components/event/VisibilityPicker'
import { RsvpSettingsFields } from '../components/event/RsvpSettingsFields'
import { MoreDetailsFields } from '../components/event/MoreDetailsFields'

function isoToLocalDatetime(iso: string): string {
  const d = new Date(iso)
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  const hours = String(d.getHours()).padStart(2, '0')
  const minutes = String(d.getMinutes()).padStart(2, '0')
  return `${year}-${month}-${day}T${hours}:${minutes}`
}

export default function EditPersonEventPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const { data: eventData, isLoading } = useEvent(id)
  const { data: occurrencesData, isLoading: occurrencesLoading } = useEventOccurrences(id)
  const updateEvent = useUpdateEvent(id!)

  const event = eventData?.data
  const eventType = event?.eventType ?? 'potluck'
  const isChurchHosted = event?.hostedByType === 'church'
  const { data: churchData } = useChurch(isChurchHosted ? event?.hostedById : undefined)
  const deleteEvent = useDeleteEvent()
  const [confirmDelete, setConfirmDelete] = useState(false)

  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [locationName, setLocationName] = useState('')
  const [addressText, setAddressText] = useState('')
  const [locationLat, setLocationLat] = useState<number | null>(null)
  const [locationLng, setLocationLng] = useState<number | null>(null)
  const [locationMode, setLocationMode] = useState<'private' | 'venue' | 'pin'>('private')
  const [audience, setAudience] = useState<'everyone' | 'denomination'>('everyone')
  const [visibility, setVisibility] = useState<'public' | 'unlisted' | 'invite_only'>('public')
  const [attributes, setAttributes] = useState<EventAttributes>({})
  const [rsvpRequiresApproval, setRsvpRequiresApproval] = useState(true)
  const [hideAddressUntilApproved, setHideAddressUntilApproved] = useState(true)
  const [maxAttendees, setMaxAttendees] = useState('')
  const [rsvpClose, setRsvpClose] = useState<RsvpCloseOption>('none')

  const [startsAt, setStartsAt] = useState('')
  const [endsAt, setEndsAt] = useState('')

  // Dish coordination
  const [dishCategories, setDishCategories] = useState<DishGoalCategory[]>([])

  // Outdoor activity
  const [outdoorDifficulty, setOutdoorDifficulty] = useState<'easy' | 'moderate' | 'strenuous'>('easy')
  const [outdoorDistance, setOutdoorDistance] = useState('')
  const [outdoorElevation, setOutdoorElevation] = useState('')
  const [outdoorMeetupNote, setOutdoorMeetupNote] = useState('')

  // Study
  const [studyTopic, setStudyTopic] = useState('')
  const [studyScripture, setStudyScripture] = useState('')

  const [imageUrl, setImageUrl] = useState<string | null>(null)
  const [initialized, setInitialized] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [geocodeError, setGeocodeError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Pre-populate form once event and first occurrence load
  const firstOccurrence = occurrencesData?.data?.[0]
  useEffect(() => {
    if (!event || !firstOccurrence || initialized) return
    setTitle(event.title)
    setDescription(event.description ?? '')
    setLocationName(event.locationName ?? '')
    setAddressText(event.addressText)
    setLocationLat(event.locationLat)
    setLocationLng(event.locationLng)
    if (event.locationMode) {
      setLocationMode(event.locationMode)
    } else if (event.locationName) {
      setLocationMode('venue')
    } else if (!event.addressText && event.locationLat && event.locationLng) {
      setLocationMode('pin')
    } else {
      setLocationMode('private')
    }
    setAudience(event.audience ?? 'everyone')
    setVisibility(event.visibility)
    setRsvpRequiresApproval(event.rsvpRequiresApproval)
    setHideAddressUntilApproved(event.hideAddressUntilApproved)
    setMaxAttendees(event.maxAttendees ? String(event.maxAttendees) : '')
    setImageUrl(event.imageUrl ?? null)
    setStartsAt(isoToLocalDatetime(firstOccurrence.startsAt))
    if (firstOccurrence.endsAt) setEndsAt(isoToLocalDatetime(firstOccurrence.endsAt))
    setRsvpClose(deriveRsvpClose(firstOccurrence.rsvpClosesAt, firstOccurrence.startsAt))

    const meta = event.metadata as Record<string, unknown> | null ?? {}
    const attrs = { ...(event.attributes ?? {}) }

    // Infer capability flags from existing metadata for backward compat
    if (meta.dishGoals) attrs.bringADish = true
    if (meta.difficulty != null || meta.distanceMiles != null) attrs.outdoorActivity = true

    setAttributes(attrs)

    if (attrs.bringADish) {
      const goals = meta.dishGoals as { categories?: DishGoalCategory[] } | undefined
      setDishCategories(goals?.categories ?? [])
    }
    if (attrs.outdoorActivity) {
      setOutdoorDifficulty((meta.difficulty as 'easy' | 'moderate' | 'strenuous') ?? 'easy')
      setOutdoorDistance(meta.distanceMiles != null ? String(meta.distanceMiles) : '')
      setOutdoorElevation(meta.elevationFt != null ? String(meta.elevationFt) : '')
      setOutdoorMeetupNote((meta.meetupNote as string) ?? '')
    }
    if (event.eventType === 'study') {
      setStudyTopic((meta.topic as string) ?? '')
      setStudyScripture((meta.scriptureRef as string) ?? '')
    }
    setInitialized(true)
  }, [event, firstOccurrence, initialized])

  function validate() {
    const errs: Record<string, string> = {}
    if (!title.trim()) errs.title = 'Title is required'
    if (!startsAt) errs.startsAt = 'Start date and time is required'
    if (!addressText.trim()) errs.addressText = 'Address is required'
    setErrors(errs)
    return Object.keys(errs).length === 0
  }

  function buildMetadata(): Record<string, unknown> | undefined {
    const meta: Record<string, unknown> = {}
    if (attributes.bringADish && dishCategories.length > 0) {
      meta.dishGoals = { categories: dishCategories }
    }
    if (attributes.outdoorActivity) {
      meta.difficulty = outdoorDifficulty
      if (outdoorDistance) meta.distanceMiles = parseFloat(outdoorDistance)
      if (outdoorElevation) meta.elevationFt = parseInt(outdoorElevation)
      if (outdoorMeetupNote.trim()) meta.meetupNote = outdoorMeetupNote.trim()
    }
    if (eventType === 'study') {
      if (studyTopic.trim()) meta.topic = studyTopic.trim()
      if (studyScripture.trim()) meta.scriptureRef = studyScripture.trim()
    }
    return Object.keys(meta).length > 0 ? meta : undefined
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (!validate()) return

    setGeocodeError('')
    setIsSubmitting(true)

    const coords = locationLat !== null && locationLng !== null
      ? { lat: locationLat, lng: locationLng }
      : await geocodeAddress(addressText.trim())

    if (!coords) {
      setGeocodeError('Could not locate that address. Use the "Locate →" button or drag the map pin.')
      setIsSubmitting(false)
      return
    }

    updateEvent.mutate(
      {
        title: title.trim(),
        description: description.trim() || undefined,
        locationName: locationName.trim() || undefined,
        addressText: addressText.trim(),
        locationLat: coords.lat,
        locationLng: coords.lng,
        locationMode,
        visibility,
        audience,
        imageUrl,
        attributes,
        rsvpRequiresApproval,
        // Hiding the address is only meaningful for a private home; venue/pin are public.
        hideAddressUntilApproved: locationMode === 'private' && rsvpRequiresApproval && hideAddressUntilApproved,
        maxAttendees: maxAttendees ? parseInt(maxAttendees) : undefined,
        metadata: buildMetadata(),
        startsAt: new Date(startsAt).toISOString(),
        endsAt: endsAt ? new Date(endsAt).toISOString() : undefined,
        rsvpClosesAt: computeRsvpClosesAt(rsvpClose, startsAt),
      },
      {
        onSuccess: () => navigate(`/events/${id}`),
        onSettled: () => setIsSubmitting(false),
      },
    )
  }

  const serverError = updateEvent.error instanceof ApiError ? updateEvent.error.message : null

  if (isLoading || occurrencesLoading || !event || !initialized) {
    return (
      <PublicLayout>
        <div className="flex items-center justify-center" style={{ height: 'calc(100vh - 80px)' }}>
          <p className="text-[13px] text-muted">Loading…</p>
        </div>
      </PublicLayout>
    )
  }

  return (
    <PublicLayout>
      <div className="w-full max-w-[600px] mx-auto px-4 sm:px-6 pt-10 pb-20 overflow-x-hidden">
        <Link
          to={`/events/${id}`}
          className="text-[13px] text-muted inline-flex items-center gap-1.5 no-underline mb-5 font-medium"
        >
          <Icons.back size={13} /> Back to event
        </Link>

        <div className="flex items-center gap-3 mb-7">
          <EventBadge type={eventType as EventType} size="md" />
          <h1 className="serif m-0 text-[36px] tracking-[-0.02em]">Edit event</h1>
        </div>

        <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">

          {(serverError || geocodeError) && (
            <div
              role="alert"
              className="px-4 py-3 rounded-xl text-[13px] bg-danger-soft border border-danger-line text-danger-deep"
            >
              {serverError ?? geocodeError}
            </div>
          )}

          {/* Details */}
          <SectionCard title="Details">
            <BasicsFields
              eventType={eventType as EventType}
              onTypeChange={() => {/* event type is fixed after creation */}}
              showTypePicker={false}
              title={title}
              onTitleChange={setTitle}
              titleError={errors.title}
              description={description}
              onDescriptionChange={setDescription}
            />
          </SectionCard>

          {/* When */}
          <SectionCard title="When">
            <EventTimePicker
              startsAt={startsAt}
              endsAt={endsAt}
              onStartsAtChange={setStartsAt}
              onEndsAtChange={setEndsAt}
              startsAtError={errors.startsAt}
            />
          </SectionCard>

          {/* Where */}
          <SectionCard title="Where">
            <LocationPicker
              addressText={addressText}
              onAddressChange={setAddressText}
              lat={locationLat}
              lng={locationLng}
              onCoordinatesChange={(lat, lng) => { setLocationLat(lat); setLocationLng(lng) }}
              addressError={errors.addressText}
              showModeToggle
              mode={locationMode}
              onModeChange={setLocationMode}
              onHideAddressChange={setHideAddressUntilApproved}
              locationName={locationName}
              onLocationNameChange={setLocationName}
            />
          </SectionCard>

          {/* Privacy & RSVP */}
          <SectionCard title="Privacy & RSVP">
            <div className="flex flex-col gap-4">
              <VisibilityPicker
                visibility={visibility}
                audience={audience}
                denomination={isChurchHosted ? null : user?.denomination}
                onChange={({ visibility: v, audience: a }) => { setVisibility(v); if (!isChurchHosted) setAudience(a) }}
              />
              <RsvpSettingsFields
                maxAttendees={maxAttendees}
                onMaxAttendeesChange={setMaxAttendees}
                rsvpClose={rsvpClose}
                onRsvpCloseChange={setRsvpClose}
                requiresApproval={rsvpRequiresApproval}
                onRequiresApprovalChange={setRsvpRequiresApproval}
                onHideAddressChange={setHideAddressUntilApproved}
                locationMode={locationMode}
              />
            </div>
          </SectionCard>

          {/* Audience — church-hosted only */}
          {isChurchHosted && churchData?.data?.denomination && churchData.data.denomination !== 'other' && (
            <SectionCard title="Audience">
              <div className="flex gap-2">
                {([
                  { value: 'everyone', label: 'Everyone', sub: 'Visible to all app users' },
                  { value: 'denomination', label: `${churchData.data.denomination.replace(/-/g, ' ')} only`, sub: 'Only users who share your denomination' },
                ] as const).map(({ value, label, sub }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setAudience(value)}
                    style={{
                      flex: 1, padding: '8px 10px', borderRadius: 10, textAlign: 'left',
                      border: `${audience === value ? 2 : 1}px solid ${audience === value ? 'var(--primary)' : 'var(--line-soft)'}`,
                      background: audience === value ? 'var(--primary-soft)' : 'var(--surface)',
                      cursor: 'pointer', fontFamily: 'inherit',
                    }}
                  >
                    <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--ink)', textTransform: 'capitalize' }}>{label}</div>
                    <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>{sub}</div>
                  </button>
                ))}
              </div>
            </SectionCard>
          )}

          {/* More details */}
          <SectionCard title="More details">
            <MoreDetailsFields
              eventType={eventType as EventType}
              attributes={attributes}
              setAttributes={setAttributes}
              dishCategories={dishCategories} setDishCategories={setDishCategories}
              outdoorDifficulty={outdoorDifficulty} setOutdoorDifficulty={setOutdoorDifficulty}
              outdoorDistance={outdoorDistance} setOutdoorDistance={setOutdoorDistance}
              outdoorElevation={outdoorElevation} setOutdoorElevation={setOutdoorElevation}
              outdoorMeetupNote={outdoorMeetupNote} setOutdoorMeetupNote={setOutdoorMeetupNote}
              studyTopic={studyTopic} setStudyTopic={setStudyTopic}
              studyScripture={studyScripture} setStudyScripture={setStudyScripture}
              imageUrl={imageUrl} setImageUrl={setImageUrl}
            />
          </SectionCard>

          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              disabled={isSubmitting}
              className="btn btn--lg flex-1"
            >
              {isSubmitting ? 'Saving…' : 'Save changes'}
            </button>
            <Link to={`/events/${id}`} className="no-underline">
              <button type="button" className="btn btn--lg btn--ghost">
                Cancel
              </button>
            </Link>
          </div>

          {/* Delete event */}
          <div className="mt-8 pt-6 border-t border-line-soft">
            {!confirmDelete ? (
              <button
                type="button"
                onClick={() => setConfirmDelete(true)}
                className="text-[13px] text-danger-DEFAULT font-medium cursor-pointer bg-transparent border-none p-0 font-sans"
              >
                Delete event…
              </button>
            ) : (
              <div className="p-4 rounded-xl border border-danger-line bg-danger-soft">
                <div className="text-[13.5px] font-semibold text-danger-deep mb-1">Delete this event?</div>
                <div className="text-[12.5px] text-danger-deep mb-3 opacity-80">
                  This removes the event and all its occurrences permanently. RSVPs will be cancelled.
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      if (!id) return
                      deleteEvent.mutate(id, {
                        onSuccess: () => navigate(isChurchHosted && event
                          ? `/churches/${churchData?.data?.slug ?? ''}/dashboard`
                          : '/'),
                      })
                    }}
                    disabled={deleteEvent.isPending}
                    className="btn btn--sm"
                    style={{ background: '#ef4444', color: '#fff', border: 'none' }}
                  >
                    {deleteEvent.isPending ? 'Deleting…' : 'Yes, delete'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmDelete(false)}
                    className="btn btn--sm btn--ghost"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>

        </form>
      </div>
    </PublicLayout>
  )
}
